/**
 * DeleteWorkspaceSection Component
 *
 * Danger zone section for permanent workspace deletion.
 * Uses ConfirmationModal with typed name confirmation to prevent accidental deletions.
 *
 * @module pages/SettingsPage/components/DeleteWorkspaceSection
 */

import { type ReactElement, memo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Stack } from '@mantine/core';
import { AlertTriangle } from 'lucide-react';

import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { NotificationService } from '@/services/notificationService';

import styles from './DeleteWorkspaceSection.module.css';

const WARNING_ICON_SIZE = 20;

interface DeleteWorkspaceSectionProps {
  readonly workspaceName: string;
  readonly workspaceId: string;
  readonly onDelete: (
    workspaceId: string
  ) => Promise<{ success: boolean; error?: string }>;
}

export const DeleteWorkspaceSection = memo(function DeleteWorkspaceSection({
  workspaceName,
  workspaceId,
  onDelete,
}: DeleteWorkspaceSectionProps): ReactElement {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const result = await onDelete(workspaceId);

    if (result.success) {
      NotificationService.showSuccess(
        t('settings.workspace.deleteWorkspace.success'),
        true
      );
      setIsModalOpen(false);
    } else {
      NotificationService.showError(
        result.error ?? t('settings.workspace.deleteWorkspace.error')
      );
    }
  }, [onDelete, workspaceId, t]);

  return (
    <>
      <div className={styles.container}>
        <Stack gap="lg">
          <h3 className={styles.dangerTitle}>
            {t('settings.workspace.deleteWorkspace.title')}
          </h3>

          <div className={styles.warningBox}>
            <AlertTriangle
              size={WARNING_ICON_SIZE}
              className={styles.warningIcon}
            />
            <p className={styles.warningText}>
              <Trans i18nKey="settings.workspace.deleteWorkspace.warning" />
            </p>
          </div>

          <Button variant="outline" color="red" onClick={handleOpenModal}>
            {t('settings.workspace.deleteWorkspace.deleteButton')}
          </Button>
        </Stack>
      </div>

      <ConfirmationModal
        opened={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        variant="danger"
        title={t('settings.workspace.deleteWorkspace.confirmTitle')}
        message={t('settings.workspace.deleteWorkspace.confirmMessage')}
        confirmLabel={t('settings.workspace.deleteWorkspace.deleteButton')}
        requireConfirmText={workspaceName}
        confirmTextLabel={t('settings.workspace.deleteWorkspace.typeToConfirm')}
      />
    </>
  );
});
